import React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { BsPrinter } from 'react-icons/bs';
import { BiMailSend } from 'react-icons/bi';

import axios from 'helpers/axios';
import config from 'helpers/config';
import InspectionTemplate from 'models/InspectionTemplateModel';
import RepairOrder from 'models/RepairOrderModel';
import Inspection from 'models/InspectionModel';
import InspectionCard from './InspectionCard';
import InspectionTaskStatus from 'models/types/InspectionTaskStatus';
import { getActivityLog } from 'actions/repairOrderActions';
import actionTypes from 'helpers/actionTypes';

interface Props {
    repairOrder: RepairOrder;

    dispatch: Dispatch;
}

interface State {
    inspectionTemplates: InspectionTemplate[];
    inspections: Inspection[];
}

class InspectionsTab extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            inspectionTemplates: [],
            inspections: [],
        };
    }

    componentDidMount() {
        this.checkAndFetchData();
    }

    componentDidUpdate(prevProps: Props) {
        // Check if the repairOrder prop is now available and was previously null/undefined
        if (!prevProps.repairOrder && this.props.repairOrder) {
            this.checkAndFetchData();
        }
    }

    checkAndFetchData = () => {
        const { repairOrder } = this.props;

        if (repairOrder) {
            this.getInspectionTemplates();
            this.getInspections();
        }
    };

    getInspectionTemplates() {
        axios
            .get(`${config.apiUrl}/inspection/templates`)
            .then((res) => this.setState({ inspectionTemplates: res.data }))
            .catch((_) => console.log(_));
    }

    getInspections() {
        axios
            .get(`${config.apiUrl}/inspection/all/${this.props.repairOrder.id}`)
            .then((res) => this.setState({ inspections: res.data }))
            .catch((_) => console.log(_));
    }

    createInspection(inspectionTemplateId: string) {
        const RO_ID = this.props.repairOrder?.id;
        axios
            .post(`${config.apiUrl}/inspection`, { inspectionTemplateId, repairOrderId: RO_ID })
            .then((response) => {
                const { dispatch } = this.props;
                dispatch<any>(getActivityLog(RO_ID));

                this.props.dispatch({
                    type: actionTypes.UPDATE_REPAIR_ORDER_SUCCESS,
                    payload: {
                        ...this.props.repairOrder,
                        inspectionsCount: !!this.props.repairOrder.inspectionsCount
                            ? ++this.props.repairOrder.inspectionsCount
                            : 1,
                    },
                });

                const { inspections } = this.state;
                inspections.unshift({ ...response.data, isNew: true });
                this.setState({ inspections });
            })
            .catch((_) => console.log(_));
    }

    // Simplified callback to refresh a single inspection after updates
    refreshInspection = (inspectionId: string) => {
        axios
            .get(`${config.apiUrl}/inspection/${inspectionId}`)
            .then((res) => {
                const { inspections } = this.state;
                const inspectionIndex = inspections.findIndex((inspection) => inspection.id === inspectionId);
                if (inspectionIndex !== -1) {
                    inspections[inspectionIndex] = res.data;
                    this.setState({ inspections });
                }
            })
            .catch((_) => console.log(_));
    };

    // Handle inspection completion
    onInspectionCompleted = (inspectionId: string, completedInspection: Inspection) => {
        const inspections = this.state.inspections;
        const inspectionIndex = inspections.findIndex((inspection) => inspection.id === inspectionId);
        if (inspectionIndex !== -1) {
            inspections[inspectionIndex] = {
                ...inspections[inspectionIndex],
                ...completedInspection,
                isCompleted: true,
            };
            this.setState({ inspections });
        }
    };

    // Handle inspection deletion
    onInspectionDeleted = (inspectionId: string) => {
        const inspections = this.state.inspections.filter((inspection) => inspection.id !== inspectionId);
        this.setState({ inspections });
    };

    render() {
        const { inspectionTemplates, inspections } = this.state;
        return (
            <div>
                <div className="flex">
                    <div className="ml-auto">
                        <button className="mr-2 border-gray-500 border text-gray-500 bg-white uppercase px-3 p-1 rounded-2xl font-medium text-sm ml-auto">
                            <BsPrinter className="inline mr-2 align-baseline" />
                            <span className="align-baseline">Print Inspection</span>
                        </button>

                        <button className="mr-2 border-gray-500 border text-gray-500 bg-white uppercase px-3 p-1 rounded-2xl font-medium text-sm ml-auto">
                            <BiMailSend className="inline mr-2 align-baseline" />
                            <span className="align-baseline">Send Inspection</span>
                        </button>
                    </div>
                </div>
                <div>
                    <div className="p-2 rounded-lg mb-6">
                        <h3 className="font-semibold text-lg">Start an inspection:</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 ">
                            {inspectionTemplates?.map((template) => {
                                return (
                                    <div
                                        key={template.id}
                                        className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 flex flex-col justify-between hover:shadow-md transition-shadow duration-200"
                                    >
                                        <div className="font-semibold text-gray-700">{template.name}</div>
                                        <small className="font-semibold text-gray-600 text-sm">
                                            {template.description}
                                        </small>
                                        <small className="text-gray-500 text-xs mt-1.5">
                                            <span className="font-semibold">{template.groups?.length}</span> inspection
                                            groups{template.tasks?.length > 1 && 's'}
                                        </small>
                                        <button
                                            className="bg-green-500 hover:bg-green-600 rounded-lg px-3 py-1.5 uppercase  font-bold w-full"
                                            onClick={() => this.createInspection(template.id)}
                                        >
                                            Start
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="grid *:grid-cols-1 sm:grid-cols-1 md:grid-cols-2  gap-4">
                        {inspections?.map((inspection, index) => {
                            return (
                                <InspectionCard
                                    inspection={inspection}
                                    key={inspection.id}
                                    onInspectionUpdated={() => this.refreshInspection(inspection.id)}
                                    onInspectionCompleted={(completedInspection) =>
                                        this.onInspectionCompleted(inspection.id, completedInspection)
                                    }
                                    onInspectionDeleted={() => this.onInspectionDeleted(inspection.id)}
                                />
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    }
}

export default connect()(InspectionsTab);
